<script lang="ts" setup>
import type { CombinedError } from '@urql/core';

import { Alert, AlertDescription } from '~ds/components/ui/alert';
import { Button } from '~ds/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '~ds/components/ui/dropdown-menu';
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';

import type { AuthUserFieldsFragment } from '~/graphql/generated/graphql';

import { COACH_ROUTE_INDEX } from '~/constants/routes/coach';
import { UNIFIED_ROUTE_EDUCATORS } from '~/constants/routes/unified';
import { RoleType } from '~/graphql/generated/graphql';

type SwitchableAccount = AuthUserFieldsFragment['switchableAccounts'][number];

/** `placement` is where the trigger sits: across the schools sidebar, or at the top of the student menu. */
interface Props {
  currentSchoolName: string;
  accounts: SwitchableAccount[];
  currentIsPersonal?: boolean;
  placement?: 'sidebar' | 'menu';
}

const props = withDefaults(defineProps<Props>(), {
  currentIsPersonal: false,
  placement: 'sidebar',
});

const triggerClasses = computed(() => twMerge(cva('-ml-2 h-auto gap-2 px-2 py-1.5', {
  variants: {
    placement: {
      sidebar: 'w-full justify-between',
      menu: 'max-w-full justify-start',
    },
  },
})({
  placement: props.placement,
})));

const { t } = useI18n();
const { switchAccount, switchingAccount } = useSchoolsSignIn();

const personalCaption = computed(() => t('schools.auth.chooseAccount.personal'));
const currentCaption = computed(() => (props.currentIsPersonal ? personalCaption.value : ''));

const CURRENT_ACCOUNT = 'current';

const open = ref(false);
const errorKey = ref<string | null>(null);

const setOpen = (value: boolean) => {
  open.value = value;
  if (!value) {
    errorKey.value = null;
  }
};

const landingPathFor = (account: SwitchableAccount) => (account.roleType === RoleType.Student ? COACH_ROUTE_INDEX : UNIFIED_ROUTE_EDUCATORS);

const errorKeyFor = (error: CombinedError | string) => {
  const rateLimited = typeof error !== 'string' && findGraphqlErrorWithCode(error, 'RATE_LIMIT_EXCEEDED');
  return rateLimited ? 'schools.accountSwitcher.errors.rateLimited' : 'schools.accountSwitcher.errors.failed';
};

const switchTo = async (account: SwitchableAccount) => {
  if (switchingAccount.value) {
    return;
  }

  setOpen(false);

  const landingPath = landingPathFor(account);
  const { error } = await switchAccount({ input: { accountId: account.accountId }, schoolName: account.schoolName, landingPath });

  if (!error) {
    // A full load, so every query and store starts over as the new account; the loader stays up until it lands.
    await navigateTo(clusterUrl(landingPath), { replace: true, external: true });
    return;
  }

  open.value = true;
  errorKey.value = errorKeyFor(error);
};
</script>

<template>
  <KUISchoolsLayoutSidebarSchool
    v-if="accounts.length === 0"
    :school-name="currentSchoolName"
    :caption="currentCaption"
  />
  <template v-else>
    <DropdownMenu :open="open" @update:open="setOpen">
      <DropdownMenuTrigger as-child>
        <Button
          variant="ghost"
          :class="triggerClasses"
          :aria-label="t('schools.accountSwitcher.trigger', { schoolName: currentSchoolName })"
          :disabled="!!switchingAccount"
          data-testid="account-switcher-trigger"
        >
          <KUISchoolsLayoutSidebarSchool :school-name="currentSchoolName" :caption="currentCaption" />
          <Icon
            :name="ICONS.arrowDown"
            size="sm"
            class="shrink-0"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="w-64">
        <!-- The DS radio item draws its indicator on the left; it sits on the right here until CP-12286. -->
        <DropdownMenuRadioGroup :model-value="CURRENT_ACCOUNT">
          <DropdownMenuRadioItem
            :value="CURRENT_ACCOUNT"
            class="min-h-11 pl-2"
            data-testid="account-switcher-current"
            @select.prevent
          >
            <template #indicator-icon>
              <span />
            </template>
            <KUISchoolsLayoutSidebarSchool
              :school-name="currentSchoolName"
              :caption="currentCaption"
              class="flex-1"
            />
            <Icon
              :name="ICONS.alertCircleSuccess"
              size="md"
              class="shrink-0 text-foreground"
              data-testid="account-switcher-current-check"
            />
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem
            v-for="account in accounts"
            :key="account.accountId"
            :value="account.accountId"
            class="min-h-11 pl-2"
            :data-testid="`account-switcher-option-${account.accountId}`"
            @select.prevent="switchTo(account)"
          >
            <KUISchoolsLayoutSidebarSchool
              :school-name="account.schoolName"
              :caption="account.isPersonal ? personalCaption : ''"
              name-weight="normal"
            />
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <Alert
          v-if="errorKey"
          variant="error"
          class="mt-1"
          aria-hidden="true"
          data-testid="account-switcher-error"
        >
          <Icon :name="ICONS.alert" />
          <AlertDescription>{{ t(errorKey) }}</AlertDescription>
        </Alert>
      </DropdownMenuContent>
    </DropdownMenu>
    <div
      class="sr-only"
      aria-live="assertive"
      aria-atomic="true"
      data-testid="account-switcher-error-announcement"
    >
      {{ errorKey ? t(errorKey) : '' }}
    </div>
  </template>
</template>
